"use client";
import { getActualSchedule } from "@cmp-src/map/InteractiveZones/helpers";
import { getVisibleAssignment } from "@gettactic/helpers";
import { isTemporarySkipMapClickOrg } from "@gettactic/helpers/src/featureFlags";
import { hasActiveResourceSchedule } from "@gettactic/helpers/src/schedules/hasActiveResourceSchedule";
import { resourceRequiresApproval } from "@gettactic/helpers/src/resources/resourceRequiresApproval";
import { useAuthenticated } from "@gettactic/hooks";
import {
  MapInteractiveZoneActionOptions,
  MapInteractiveZones
} from "@gettactic/map-builder/src/types";
import React from "react";
import { useMap } from "@/features/map/MapProvider";
import { useIsMultiRoomSelectionEnabled } from "@/features/reserve/hooks/useIsMultiRoomSelectionEnabled";
import { useRoomSelection } from "@/features/reserve/providers/roomSelectionContext";

/**
 * Builds the native `MapInteractiveZones` record consumed by the map-builder
 * viewer (`InteractiveZoneSVG` / `InteractiveZone`). This replaces the DOM
 * clone overlay (`MapViewSvgInteractiveZones`) for map-builder maps: spot
 * states, selection and clicks are driven by this data instead of CSS classes
 * applied to scraped SVG markup. Legacy `map_url` maps keep using the overlay.
 *
 * Returns `undefined` when the current floor is not a map-builder map.
 */
export const useMapBuilderInteractiveZones = ():
  | MapInteractiveZones
  | undefined => {
  const {
    isMapBuilderMap,
    zonesInfo,
    schedulable,
    resources,
    startDate,
    selectedResource,
    selectResource,
    startReserve,
    isOnReservationModal,
    resourceInReservation,
    poppers,
    events
  } = useMap();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const isMultiRoomSelectionEnabled = useIsMultiRoomSelectionEnabled();
  const { rooms: selectedRooms, isPickingRoom, addRoom } = useRoomSelection();
  const isPickingEnabled = isMultiRoomSelectionEnabled && isPickingRoom;
  const selectedRoomIds = isMultiRoomSelectionEnabled
    ? selectedRooms.map((room) => room.id)
    : [];

  const temporarySingleMapClick = isTemporarySkipMapClickOrg(authenticatedUser);
  const authUserId = authenticatedUser?.user?.id;
  const lastAccessedDeskResourceId =
    authenticatedUser?.user?.organization_user
      ?.last_accessed_desk_resource_id ?? null;
  const lastAccessedRoomResourceId =
    authenticatedUser?.user?.organization_user
      ?.last_accessed_meeting_room_resource_id ?? null;

  return React.useMemo(() => {
    if (!isMapBuilderMap) return undefined;

    const record: MapInteractiveZones = {};

    // Iterate the data-driven zones (one per spot with a resource_id) rather
    // than `schedulable` so spots whose resource isn't schedulable still get
    // an entry and render as not-assigned — same as the overlay did.
    for (const zone of zonesInfo.zones) {
      const zoneId = zone.id;
      const res = schedulable?.find((x) => x.id === zoneId);
      const notAssigned = !res;
      const requiresApproval =
        !!res && resourceRequiresApproval(res) && res.available_slots > 0;
      const disabled = !!res && res.available_slots === 0;
      const reservedSlots = res?.reserved_slots || 0;
      const isWalkUp = !!res?.is_walkup;
      const hasSchedules = hasActiveResourceSchedule(res?.schedules, isWalkUp);
      // Some schedule payloads carry the user on the slot instead of the
      // schedule itself — normalize like the overlay did.
      const schedules = (res?.schedules || []).map((schedule) => ({
        ...schedule,
        user: schedule.user || schedule.slots?.[0]?.user || null
      }));
      const actualSchedule = getActualSchedule(schedules as never, startDate);
      const scheduledUserId = schedules[0]?.user?.id;
      // Walk-up occupancy never flips `disabled` (see hasSchedules above) —
      // use the same corrected occupancy signal here so the occupant
      // avatar can show for a walk-up check-in too.
      const isOccupied = isWalkUp ? hasSchedules : disabled;
      const showCircleProfilePhoto = !!(
        isOccupied &&
        hasSchedules &&
        actualSchedule &&
        res &&
        (res.type === "desk" || res.type === "parking_space") &&
        res.total_slots === 1
      );
      const assignedUser = getVisibleAssignment(resources, zoneId);

      const onAction = (options?: MapInteractiveZoneActionOptions) => {
        // While picking a room to add, a click only feeds the selection — it must
        // not hand the clicked room over to the reserve flow. A room the reservation
        // already holds has nothing left to add, so it is left alone.
        if (isPickingEnabled) {
          if (
            res?.type === "meeting_room" &&
            !selectedRoomIds.includes(zoneId)
          ) {
            addRoom(res);
          }
          return;
        }

        poppers.setSelectedResourceCoords({
          x: options?.x || 0,
          y: options?.y || 0,
          width: options?.width || 0,
          height: options?.height || 0
        });

        // Walk-up resources are never reservable from the app — their
        // occupancy is managed externally via the API. Every branch below
        // that would hand the resource to the reserve flow falls back to
        // the read-only details view instead.
        if (isOnReservationModal) {
          // Match the overlay: clicking while the reserve modal is open
          // highlights the spot and hands the resource to the reserve flow.
          events.trigger("onHighlightResource", res || null);
          if (res && !isWalkUp) {
            startReserve(res);
          } else if (isWalkUp) {
            selectResource(zoneId, false);
          }
          return;
        }

        if (options?.isCmdOrCtrl) {
          // Cmd/Ctrl-click opens the reservation directly (overlay parity).
          if (res && !isWalkUp) {
            startReserve(res);
          } else if (isWalkUp) {
            selectResource(zoneId, false);
          }
          return;
        }

        if (!temporarySingleMapClick || isWalkUp) {
          selectResource(zoneId, false);
        } else if (res) {
          startReserve(res);
        }
      };

      record[zoneId] = {
        zoneId,
        notAssigned,
        requiresApproval,
        disabled,
        isOccupied,
        reservedSlots,
        hasSchedules,
        actualSchedule,
        scheduledUserId,
        showCircleProfilePhoto,
        assignedUser,
        isDedicated: !!res?.is_dedicated,
        isWalkUp,
        showPulse: !!scheduledUserId && scheduledUserId === authUserId,
        isLastUsed:
          lastAccessedDeskResourceId === zoneId ||
          lastAccessedRoomResourceId === zoneId,
        isSelected:
          selectedResource?.id === zoneId ||
          (isOnReservationModal && resourceInReservation === zoneId) ||
          selectedRoomIds.includes(zoneId),
        // Only rooms can join the reservation being composed — everything else
        // reads as unavailable while the map is armed.
        isNotSelectable: isPickingEnabled && res?.type !== "meeting_room",
        // Numbering only makes sense once the reservation holds more than one room.
        selectionPosition:
          selectedRoomIds.length > 1 && selectedRoomIds.includes(zoneId)
            ? selectedRoomIds.indexOf(zoneId) + 1
            : undefined,
        lastAccessedDeskResourceId,
        lastAccessedRoomResourceId,
        onAction
      };
    }

    return record;
  }, [
    isMapBuilderMap,
    zonesInfo.zones,
    schedulable,
    resources,
    startDate,
    selectedResource?.id,
    isOnReservationModal,
    resourceInReservation,
    authUserId,
    lastAccessedDeskResourceId,
    lastAccessedRoomResourceId,
    temporarySingleMapClick,
    selectResource,
    startReserve,
    poppers,
    events,
    isPickingEnabled,
    addRoom,
    // Joined rather than the array itself: a fresh array every render would
    // rebuild the whole record on each pass.
    selectedRoomIds.join(",")
  ]);
};
